import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

const ACCORDION_COLUMN_SPLIT_CLASS = 'accordion--column-split-active';

/**
 * @param {HTMLElement} left
 * @param {HTMLElement} right
 * @param {HTMLElement} source
 */
function mergeAccordionColumnNodes(left, right, source) {
  const leftNodes = [...left.children];
  const rightNodes = [...right.children];

  while (leftNodes.length > 0 || rightNodes.length > 0) {
    if (leftNodes.length > 0) source.appendChild(/** @type {Node} */ (leftNodes.shift()));
    if (rightNodes.length > 0) source.appendChild(/** @type {Node} */ (rightNodes.shift()));
  }
}

function updateAccordionColumnSplit() {
  const roots = document.querySelectorAll('.accordion--rows-grid[data-accordion-column-split]');

  for (const root of roots) {
    if (!(root instanceof HTMLElement)) continue;

    const source = root.querySelector('.accordion__split-source');
    const left = root.querySelector('[data-accordion-column-left]');
    const right = root.querySelector('[data-accordion-column-right]');

    if (!(source instanceof HTMLElement) || !(left instanceof HTMLElement) || !(right instanceof HTMLElement)) {
      continue;
    }

    const wantsSplit = mediaQueryLarge.matches;
    const isSplit = root.classList.contains(ACCORDION_COLUMN_SPLIT_CLASS);

    if (wantsSplit && !isSplit) {
      const wrappers = [...source.children];
      if (wrappers.length === 0) continue;

      let autoIndex = 0;

      wrappers.forEach((wrapper) => {
        if (!(wrapper instanceof HTMLElement)) return;

        /** @type {HTMLElement | null} */
        const descendantSideSource = wrapper.querySelector('[data-row-side]');
        const side =
          wrapper.dataset.rowSide ||
          descendantSideSource?.dataset.rowSide ||
          'auto';

        /** @type {HTMLElement} */
        let target;

        if (side === 'left') {
          target = left;
        } else if (side === 'right') {
          target = right;
        } else {
          target = autoIndex % 2 === 0 ? left : right;
          autoIndex += 1;
        }

        target.appendChild(wrapper);
      });

      root.classList.add(ACCORDION_COLUMN_SPLIT_CLASS);
    } else if (!wantsSplit && isSplit) {
      mergeAccordionColumnNodes(left, right, source);
      root.classList.remove(ACCORDION_COLUMN_SPLIT_CLASS);
    }
  }
}

let accordionColumnSplitMediaListenerAttached = false;

function initAccordionColumnSplit() {
  updateAccordionColumnSplit();

  if (!accordionColumnSplitMediaListenerAttached) {
    mediaQueryLarge.addEventListener('change', updateAccordionColumnSplit);
    accordionColumnSplitMediaListenerAttached = true;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccordionColumnSplit, { once: true });
} else {
  initAccordionColumnSplit();
}
